/**
 * @fileoverview Hook de Data Layer para gesti?n y agrupaci?n de respuestas r?pidas por equipos y usuario.
 * Implements strict separation of responsibilities, optimization of rendering and dynamic grouping.
 *
 * @module useFastAnswers
 * @category Hook - Data Layer
 * @since 1.0.0
 * @version 1.1.0 - Documentaci?n JSDoc completa y optimizaci?n de memoizaci?n.
 */

import {
  useEffect, useState, useCallback, useMemo
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  omitBy, isNil, filter, forEach, find, isEmpty
} from 'lodash';
import {
  getMessengerFastAnswers as getMessengerFastAnswersAction,
  getInstagramFastAnswers as getInstagramFastAnswersAction
} from "../../../../actions/actions";
import { getWhatsAppFastAnswers as getWhatsAppFastAnswersAction } from "../../../../actions/whatsapp_karix";
import { getFastAnswersForComments as getFastAnswersForCommentsAction, getTikTokFastAnswers as getTikTokFastAnswersAction } from "../../../../actions/fastAnswers";
import getTeamsIfEmpty from '../../../../actions/inbox/teamActions';
import PLATFORM_CONFIG from '../../../utils/platformConfig';
import { resolvePlatformChannelKey } from '../../Integrations/platformChannelIntegration';
import { sortQuickAnswersBySearch } from '../../../../util/quickAnswerSearchRanking';

/**
 * Team 0 or '0' is treated as no filter (equivalent to null). 'all' and 'owned' are preserved.
 * @param {string|number|undefined|null} teamValue
 * @returns {string|number|undefined}
 */
function normalizeTeamFilter(teamValue) {
  if (teamValue === 'all' || teamValue === 'owned') return teamValue;
  if (teamValue == null || teamValue === '') return undefined;
  if (Number(teamValue) === 0) return undefined;
  return teamValue;
}

/**
 * @typedef {Object} UseFastAnswersParams
 * @description Input parameters for the useFastAnswers hook.
 * @property {string} searchTerm - Search term to filter answers
 * @property {string} type - Quick answer type
 * @property {string|number} customer - Customer ID
 * @property {string|number} team - Team ID or 'all'/'owned'. 0 or '0' is equivalent to no filter (null).
 * @property {string} platform - Platform ('whatsapp', 'facebook', 'instagram', etc.)
 */

/**
 * Hook of Data Layer for management and grouping of quick answers by teams and user.
 *
 * Implements the **Data Layer** pattern with strict separation of responsibilities:
 * - Only manages loading, grouping and state of quick answers
 * - Does not manage UI logic or business operations
 * - Optimized with useCallback and useMemo for performance
 *
 * @function useFastAnswers
 * @category Hook - Data Layer
 *
 * @param {UseFastAnswersParams} params - Input parameters for the useFastAnswers hook
 *
 * @returns {Object} Object with grouped answers, loading state, teams and control functions
 * @property {Array} groupedFastAnswers - Quick answers grouped by team/user
 * @property {boolean} isLoading - Loading state
 * @property {function} resetFastAnswers - Function to reset answers
 * @property {function} refetch - Function to reload answers
 * @property {Array} teams - List of available teams
 *
 * @example
 * const {
 *   groupedFastAnswers,
 *   isLoading,
 *   resetFastAnswers,
 *   refetch,
 *   teams
 * } = useFastAnswers({ searchTerm, type, customer, team, platform });
 *
 * @see {@link ../../../../actions/actions} Actions to load quick answers
 * @see {@link ../../../../actions/inbox/teamActions} Action to load teams
 *
 * @since 1.0.0
 * @version 1.1.0 - Complete JSDoc documentation and optimization
 */
const useFastAnswers = ({
  searchTerm,
  type,
  customer,
  team,
  platform
}) => {
  const { t } = useTranslation('FastAnswer');
  const dispatch = useDispatch();
  const { teams } = useSelector((reduxState) => reduxState.inboxTeamsReducer);
  const [fastAnswers, setFastAnswers] = useState([]);
  const [groupedFastAnswers, setGroupedFastAnswers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const { fast_answers } = useSelector((reduxState) => reduxState.mainReducer);

  const normalizedTeam = useMemo(() => normalizeTeamFilter(team), [team]);

  /**
   * Effect to load teams when mounting the hook.
   * @memberof useFastAnswers
   */
  useEffect(() => {
    dispatch(getTeamsIfEmpty({ owned: true }));
  }, [dispatch]);

  /**
   * Function to load quick answers according to platform and filters.
   * @function fetchFastAnswers
   * @memberof useFastAnswers
   * @returns {Promise<void>} Loads quick answers and updates state
   */
  const fetchFastAnswers = useCallback(async () => {
    if (!platform) return;
    setIsLoading(true);
    const params = omitBy({
      search: searchTerm,
      type,
      team: normalizedTeam,
      customer
    }, isNil);
    const channelKey = resolvePlatformChannelKey(platform) || platform;
    try {
      switch (channelKey) {
        case PLATFORM_CONFIG.whatsapp.platformKey:
          dispatch(getWhatsAppFastAnswersAction(params));
          break;
        case PLATFORM_CONFIG.messenger.platformKey:
        case PLATFORM_CONFIG.facebook_messages.platformKey:
          dispatch(getMessengerFastAnswersAction(params));
          break;
        case PLATFORM_CONFIG.instagram.platformKey:
          dispatch(getInstagramFastAnswersAction(params));
          break;
        case PLATFORM_CONFIG.facebook_comments.platformKey:
        case PLATFORM_CONFIG.instagram_comments.platformKey:
          dispatch(getFastAnswersForCommentsAction(params, platform));
          break;
        case PLATFORM_CONFIG.tik_tok_messages.platformKey:
        case PLATFORM_CONFIG.tik_tok_comments.platformKey:
          dispatch(getTikTokFastAnswersAction(params, platform));
          break;
        default:
          break;
      }
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, type, normalizedTeam, platform, customer, dispatch]);

  /**
   * Effect to load quick answers when changing filters.
   * @memberof useFastAnswers
   */
  useEffect(() => {
    fetchFastAnswers();
  }, [fetchFastAnswers]);

  /**
   * Effect to update quick answers from the store.
   * @memberof useFastAnswers
   */
  useEffect(() => {
    setFastAnswers(sortQuickAnswersBySearch(fast_answers, searchTerm));
  }, [fast_answers, searchTerm]);

  /**
   * Groups quick answers by team, personal and global.
   * @function groupQuickAnswersByTeams
   * @memberof useFastAnswers
   */
  const groupQuickAnswersByTeams = useCallback(() => {
    const sameTeamId = (a, b) => a != null && b != null && String(a) === String(b);

    const allFastAnswers = {
      id: 'all',
      name: t('allAnswers'),
      color: '#7BD6A2',
      quick_answers: []
    };
    const personalFastAnswers = {
      id: 'owned',
      name: t('personalAnswers'),
      color: '#3CAAE1',
      quick_answers: []
    };
    const ungroupedFastAnswers = {
      id: 'ungrouped',
      name: t('ungroupedAnswers'),
      color: '#9CA3AF',
      quick_answers: []
    };
    let newGroups = teams?.map((teamItem) => {
      const { id, name, color } = teamItem;
      return {
        id,
        name,
        color,
        quick_answers: []
      };
    }) || [];
    forEach(fastAnswers, (answer) => {
      const teamInAnswer = answer.attributes?.team;
      const groupIn = teamInAnswer
        ? find(newGroups, (g) => sameTeamId(g.id, teamInAnswer.id))
        : null;

      if (groupIn) {
        groupIn.quick_answers.push({ ...answer });
      } else if (answer.attributes?.global) {
        allFastAnswers.quick_answers.push({ ...answer });
      } else if (answer.attributes?.retailer_user_id) {
        personalFastAnswers.quick_answers.push({ ...answer });
      } else {
        ungroupedFastAnswers.quick_answers.push({ ...answer });
      }
    });
    const teamGroupMode = normalizedTeam == null ? 'all' : normalizedTeam;
    if (teamGroupMode === 'all') {
      newGroups = [allFastAnswers, personalFastAnswers].concat(newGroups, [ungroupedFastAnswers]);
    } else if (teamGroupMode === 'owned') {
      newGroups = [personalFastAnswers].concat(newGroups);
    } else {
      newGroups = newGroups.filter((teamItem) => sameTeamId(teamItem.id, normalizedTeam));
    }
    newGroups = filter(newGroups, (group) => !isEmpty(group.quick_answers));
    setGroupedFastAnswers(newGroups);
  }, [fastAnswers, teams, normalizedTeam, t]);

  /**
   * Effect to group quick answers when changing data/teams.
   * @memberof useFastAnswers
   */
  useEffect(() => {
    groupQuickAnswersByTeams();
  }, [groupQuickAnswersByTeams]);

  /**
   * Resets the state of quick answers and grouped answers.
   * @function resetFastAnswers
   * @memberof useFastAnswers
   */
  const resetFastAnswers = useCallback(() => {
    setFastAnswers([]);
    setGroupedFastAnswers([]);
  }, []);

  /**
   * Memoizes the return object for performance.
   * @memberof useFastAnswers
   * @returns {Object} Object with grouped answers, state and functions
   */
  return useMemo(() => ({
    groupedFastAnswers,
    isLoading,
    resetFastAnswers,
    refetch: fetchFastAnswers,
    teams,
    fastAnswers
  }), [
    groupedFastAnswers,
    isLoading,
    resetFastAnswers,
    fetchFastAnswers,
    teams,
    fastAnswers
  ]);
};

export default useFastAnswers;
